import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Sparkles } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { LANGS } from "../../lib/editions";
import { Flag } from "./Flag";
import { avatarUrl } from "../../lib/avatars";

export const PokemonMenu = () => {
  const { t, tr, lang } = useLang();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={close}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-[#F2C230] hover:text-white transition-colors h-11" data-testid="nav-pokemon-menu-btn" aria-expanded={open}>
        Pokémon TCG <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full w-[720px] rounded-2xl bg-[#120E1F] border border-[#8A5CD6]/40 shadow-[0_30px_60px_-20px_rgba(0,0,0,.6)] p-6 grid grid-cols-3 gap-6 fade-up z-50" data-testid="pokemon-mega-menu">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#F2C230] mb-3">{tr("Nach Sprache shoppen", "Shop by language")}</p>
            {LANGS.map((l) => <Link key={l.code} to={`/pokemon/${l.slug}`} onClick={close} data-testid={`menu-lang-${l.slug}`} className="flex items-center gap-3 py-1.5 text-sm text-white/80 hover:text-[#F2C230] transition-colors"><Flag code={l.code} />{lang === "en" ? l.en : l.de}</Link>)}
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#F2C230] mb-3">{tr("Nach Produktart", "By product type")}</p>
            {Object.keys(t("cat")).map((c) => <Link key={c} to={`/shop?category=${c}`} onClick={close} data-testid={`menu-cat-${c}`} className="block py-1.5 text-sm text-white/80 hover:text-[#F2C230] transition-colors">{t("cat")[c]}</Link>)}
          </div>
          <div className="rounded-xl bg-gradient-to-br from-[#6E3FB8] to-[#241A3A] p-5 flex flex-col justify-between">
            <div><Sparkles className="h-5 w-5 text-[#F2C230]" /><p className="font-display text-2xl text-white mt-2">{tr("Alle Pokémon-Produkte", "All Pokémon products")}</p>
              <p className="text-xs text-white/60 mt-1">{tr("Jede Edition klar gekennzeichnet – nie wieder die falsche Sprache.", "Every edition clearly labelled – never the wrong language again.")}</p></div>
            <Link to="/shop" onClick={close} className="btn-gold mt-4 text-sm" data-testid="menu-all-products">{tr("Alle ansehen", "View all")} →</Link>
          </div>
        </div>
      )}
    </div>
  );
};

export const MobileLangLinks = ({ onClick }) => {
  const { tr, lang } = useLang();
  return (
    <div className="space-y-1" data-testid="mobile-lang-links">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#F2C230] pt-1">{tr("Nach Sprache shoppen", "Shop by language")}</p>
      <div className="grid grid-cols-2 gap-1">{LANGS.map((l) => <Link key={l.code} to={`/pokemon/${l.slug}`} onClick={onClick} data-testid={`mobile-lang-${l.slug}`} className="flex items-center gap-2 text-sm text-white/80 py-1.5"><Flag code={l.code} />{lang === "en" ? l.en : l.de}</Link>)}</div>
    </div>
  );
};

export const LanguageTiles = () => {
  const { tr, lang } = useLang();
  return (
    <section className="bg-[#120E1F] grain" data-testid="language-tiles">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><h2 className="font-display text-4xl sm:text-5xl text-white">{tr("Pokémon nach Sprache entdecken", "Discover Pokémon by language")}</h2>
            <p className="text-white/60 mt-2 text-sm">{tr("Wähle deine bevorzugte Sprache und entdecke passende Sets, Displays, Booster und mehr.", "Pick your preferred language and discover matching sets, displays, boosters and more.")}</p></div>
          <Link to="/shop" className="text-sm text-[#C9B6F2] hover:text-[#F2C230] transition-colors">{tr("Alle anzeigen", "View all")} →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {LANGS.map((l, i) => (
            <Link key={l.code} to={`/pokemon/${l.slug}`} data-testid={`lang-tile-${l.slug}`} style={{ animationDelay: `${i * 70}ms` }}
              className="fade-up group relative overflow-hidden rounded-2xl border border-[#8A5CD6]/30 bg-[#241A3A] min-h-[190px] sm:min-h-0 sm:aspect-[16/10] hover:border-[#F2C230]/70 transition-colors">
              <img src={avatarUrl(l.avatar)} alt="" className="absolute right-0 top-0 h-full w-3/5 object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-[opacity,transform] duration-700 [mask-image:linear-gradient(to_left,black_40%,transparent)]" />
              <div className="relative h-full p-4 sm:p-5 flex flex-col justify-end">
                <Flag code={l.code} className="h-6 w-9 mb-2 shrink-0" />
                <p className="font-display text-xl sm:text-3xl text-white leading-tight">{lang === "en" ? l.en : l.de}</p>
                <p className="text-xs text-white/60 mt-1 max-w-[75%] sm:max-w-[60%]">{lang === "en" ? l.tagEn : l.tagDe}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
